import {
  getRegionByLanguage,
  GoogleApi,
  IGoogleSessionToken,
  normalizeGoogleTimestamp,
  SupportedLanguage,
} from "@ryde/shared";

import { GOOGLE_TILE_SESSION_RETRY } from "./google-api-loader.constants";

export const getGoogleSessionStorageKey = (
  language: SupportedLanguage,
): `googleSession_${SupportedLanguage}` => `googleSession_${language}`;

export const isCachedGoogleSessionValid = (
  savedSession: IGoogleSessionToken | null | undefined,
): savedSession is IGoogleSessionToken => {
  if (!savedSession?.session || !savedSession.expiry) {
    return false;
  }

  return (
    Number(new Date()) < Number(normalizeGoogleTimestamp(savedSession.expiry))
  );
};

const wait = (delayMs: number): Promise<void> =>
  new Promise(resolve => {
    setTimeout(resolve, delayMs);
  });

export const fetchGoogleTileSessionWithRetry = async ({
  language,
  shouldAbort,
  maxAttempts = GOOGLE_TILE_SESSION_RETRY.MAX_ATTEMPTS,
  retryDelayMs = GOOGLE_TILE_SESSION_RETRY.DELAY_MS,
}: {
  language: SupportedLanguage;
  shouldAbort: () => boolean;
  maxAttempts?: number;
  retryDelayMs?: number;
}): Promise<IGoogleSessionToken> => {
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (shouldAbort()) {
      throw new Error("Google tile session fetch aborted");
    }

    try {
      return await GoogleApi.createTileSession({
        language,
        region: getRegionByLanguage(language),
      });
    } catch (error) {
      lastError = error;

      if (attempt < maxAttempts && !shouldAbort()) {
        await wait(retryDelayMs);
      }
    }
  }

  throw lastError;
};
